/** IXD Preview 렌더링 컴포넌트 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { cn } from "@/lib/utils";
import type { IxdChunk } from "@/apis/compare/route";
import type { FsType } from "@/apis/ixd-editor/types";
import {
  clearFocusedCell,
  IXD_SECTION_SELECTOR,
} from "@/utils/ixd-dsd-compare/focus-utils";
import type {
  CellChangeOrigin,
  CellIdentityLocation,
} from "@/utils/ixd-cell-model";
import { useIxdCellSelection } from "./use-ixd-cell-selection";
import { collectIxdCellsForHeader } from "@/utils/ixd-dsd-compare/header-click-targets";
import {
  exactCellIdSelector,
  isPeriodlessCellId,
  periodAgnosticCellIdSelector,
} from "@/utils/ixd-dsd-compare/cell-id-selector";
import { Chunk } from "./preview/chunk";
import { CellLinkagePopover } from "./preview/cell-linkage-popover";
import {
  IxdPreviewConfigProvider,
  type IxdCellNotice,
} from "./preview/ixd-preview-config-context";
import { useIxdCellReviewStyles } from "./use-ixd-cell-review-styles";
import { useIxdCellEditHandlers } from "./use-ixd-cell-edit-handlers";
import {
  type FocusIdentityLocationOptions,
  useIxdSameValuePopover,
} from "./use-ixd-same-value-popover";
import type { ModifiedCellDisplayValues } from "./cell-linkage-floating-panel";

interface IxdPreviewRendererProps {
  chunks: IxdChunk[];
  /** 원본 chunk ID와 분리된 DOM 전용 렌더 스코프. 단일 chunk 렌더러에서만 사용한다. */
  renderScopeId?: string;
  /** 잘라낸 dlView의 첫 항목이 원본 chunk에서 갖던 index */
  sourceDlViewIndexOffset?: number;
  selectedChunkId?: string | null;
  /** 셀 편집 모드 활성화 */
  editable?: boolean;
  /** identity key → 셀 위에 표시할 알림 */
  cellNotices?: ReadonlyMap<string, IxdCellNotice>;
  /** 변경된 셀 값 (cellId → newValue) */
  cellChanges?: Record<string, string>;
  /** 변경 key → 변경 출처. 수동 변경 셀의 모서리 표시에 사용한다. */
  changeOrigins?: Record<string, CellChangeOrigin>;
  /** normalized cellId → metadata identity key */
  cellIdentityByNormalizedCellId?: Map<string, string>;
  /** metadata identity key → 미저장 변경값 */
  identityChanges?: Map<string, string>;
  /** metadata identity key → 동일 identity value가 나타나는 보고서 위치 목록 */
  identityLocationsByKey?: Map<string, CellIdentityLocation[]>;
  /**
   * 셀 색·모서리 표식의 뜻을 셀 하단에 한 줄로 적는다. IXD 값 편집기(워크북 비교
   * 여부와 무관) 전용 — 읽기 전용 미리보기·대사 화면에서는 행 높이만 늘어난다.
   */
  showCellFootnotes?: boolean;
  /** 연동 셀 목록에서 특정 위치를 클릭했을 때 이 렌더러 밖(다른 DSD 대목차)으로의 이동을 맡길 수 있음 */
  onFocusIdentityLocation?: (location: CellIdentityLocation) => boolean | void;
  /** chunkId → 그 chunk가 속한 fsType. 편집 호출 시 fsType을 명시적으로 주입한다. */
  chunkFsTypeById?: Map<string, FsType>;
  /** 셀 편집 콜백 (단건) */
  onCellEdit?: (
    fsType: FsType,
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
  /** 셀 입력 모드 진입 시 호출 */
  onCellEditStart?: () => void;
  /**
   * 셀 편집 콜백 (벌크 — undo 한 그룹).
   * entries에 셀별 fsType이 함께 담기므로 한 paste/delete가 양쪽 fsType에
   * 걸쳐도 호출이 한 번에 처리된다.
   * 반환값은 반영되지 않은 entries 인덱스 (연동 셀 충돌 등).
   */
  onCellEditBatch?: (
    entries: Array<{
      fsType: FsType;
      cellId: string;
      newValue: string;
      identityKey?: string;
      locationUid?: string;
    }>,
  ) => number[] | void;
  /** 편집 입력창에 원본 원값 표시 여부 */
  showRawValueInEdit?: boolean;
  /** Ctrl+Z 되돌리기 콜백 */
  onUndo?: () => void;
  /** IXD 셀 직접 클릭 시 외부(useIxdFocus)의 프로그래매틱 포커스 강조를 해제하는 콜백 */
  onClearFocus?: () => void;
  /** 단일 chunk 렌더러에서도 첫 대목차 위 구분선을 표시해야 하는 경우 */
  showLeadingChunkSeparator?: boolean;
  /** 기간 섹션 라벨 표시 여부. 본문/주석 판별은 호출부가 명시적으로 제어한다. */
  showPeriodLabels?: boolean;
  /**
   * 본문 끝 40px 여백. 화면 전체를 채우는 뷰어에서는 마지막 표가 바닥에 붙지 않게
   * 띄워 주지만, 카드 칸 안에 끼워 쓸 때는 칸이 가진 여백 위에 덧붙어 위아래가 어긋난다.
   */
  showBottomSpacer?: boolean;
  /** 전전기 기간 섹션을 숨겨야 하는 화면에서 호출부가 명시적으로 제어한다. */
  hideBeforePreviousPeriod?: boolean;
  /**
   * 선택된 IXD cellId 목록 변경 콜백. 일반 보기 모드(편집 의도 없음)에서 DSD 강조(dim)용으로
   * 전파된다. 편집 모드에서는 호출되지 않는다.
   */
  onSelectedCellIdsChange?: (
    cellIds: string[],
    identityKeys?: Array<string | null>,
    dlViewIds?: Array<string | null>,
  ) => void;
  /**
   * 사용자가 직접 수정한 셀이면 기존/수정 표시값을 반환. 넘기면 수정 셀 선택 시
   * 연동 popover 하단(연동이 없으면 단독 `modified` 모드)에 값이 노출된다.
   */
  getModifiedValues?: (cell: HTMLElement) => ModifiedCellDisplayValues | null;
  /** DSD 선택에 매칭되는 IXD cellId — counterpart 외곽선 */
  counterpartCellIds?: string[];
  /** DSD 선택에 매칭되는 fact identity key — 축 충돌 셀을 구분한 counterpart 외곽선 */
  counterpartIdentityKeys?: string[];
  /** 전체 매칭된 IXD 원본 cellId 목록 — 매칭 DSD 없는 셀 빨간 강조 계산용 */
  matchedCellIds?: string[];
  /** 매칭 DSD 가 없는 IXD 값 셀을 상시 빨간색으로 강조 */
  showUnmatchedStatus?: boolean;
  /**
   * 컨테이너 클래스 override. 기본은 자체 세로 스크롤(overflow-auto)이지만,
   * 바깥 컨테이너가 스크롤을 담당하는 화면(비교 행 카드)에서는 `overflow-clip`을
   * 넘겨 내부 표의 sticky 가로 스크롤바가 바깥 스크롤러를 따라가게 한다.
   */
  className?: string;
}

// 외부에서 호출 가능한 메서드 인터페이스
export interface IxdPreviewRendererRef {
  /** IXD 셀 ID로 직접 DOM 요소 검색 — cell 타입 매칭의 primary path */
  searchByCellIds: (
    cellIds: string[],
    expectedIdentityKeysByCellId?: Map<string, string[]>,
  ) => HTMLElement[];
  /**
   * DSD 헤더 태그(lineItem/period/member)에 대응하는 IXD 헤더(th) 요소를 찾는다.
   * lineItem·member 는 `data-header-ids` element id 로, 기간은 `data-header-period`
   * 로 매칭한다. 우선순위: 라인아이템 > 축 멤버 > 기간.
   */
  searchHeaderByTag: (tag: {
    lineItem?: string | null;
    period?: string | null;
    member?: string | null;
  }) => HTMLElement[];
  activateSameValueForCell: (cell: HTMLElement) => boolean;
  focusCellLocation: (
    location: CellIdentityLocation,
    options?: FocusIdentityLocationOptions,
  ) => boolean;
  clearFocus: () => void;
  scrollToChunk: (chunkId: string) => void;
  /** 스크롤 컨테이너 (스크롤 스파이용) */
  getScrollContainer: () => HTMLDivElement | null;
}

/** content-visibility 로 늘어난 높이를 따라가며 재정렬하는 최대 프레임 수 */
const CHUNK_SCROLL_MAX_ALIGN_FRAMES = 12;

const CHUNK_HIGHLIGHT_RESET_DELAY_MS = 1500;

/**
 * IxdPreviewRenderer 메인 컴포넌트
 */
export const IxdPreviewRenderer = forwardRef<
  IxdPreviewRendererRef,
  IxdPreviewRendererProps
>(
  (
    {
      chunks,
      renderScopeId,
      sourceDlViewIndexOffset,
      selectedChunkId,
      editable: editableProp,
      cellNotices,
      cellChanges,
      changeOrigins,
      cellIdentityByNormalizedCellId,
      identityChanges,
      identityLocationsByKey,
      showCellFootnotes,
      onFocusIdentityLocation,
      chunkFsTypeById,
      onCellEdit,
      onCellEditStart,
      onCellEditBatch,
      showRawValueInEdit,
      onUndo,
      onClearFocus,
      showLeadingChunkSeparator = false,
      showPeriodLabels = true,
      showBottomSpacer = true,
      hideBeforePreviousPeriod = false,
      onSelectedCellIdsChange,
      getModifiedValues,
      counterpartCellIds,
      counterpartIdentityKeys,
      matchedCellIds,
      showUnmatchedStatus,
      className,
    },
    ref,
  ) => {
    const editable = !!editableProp;
    const containerRef = useRef<HTMLDivElement>(null);
    const focusedCellRef = useRef<HTMLElement | null>(null);
    const selectCellRef = useRef<
      ((uniqueId: string, options?: { scroll?: boolean }) => boolean) | null
    >(null);

    // DSD 선택에 매칭되는 IXD 셀 강조 스타일을 이 렌더러 컨테이너로만 한정하기 위한 고유 scope id.
    const reviewStyleScopeId = useIxdCellReviewStyles({
      counterpartCellIds,
      counterpartIdentityKeys,
      matchedCellIds,
      showUnmatchedStatus,
    });

    const currentChunkIds = useMemo(
      () => new Set(chunks.map((chunk) => chunk.id)),
      [chunks],
    );

    const {
      cellPopover,
      setCellPopover,
      cellPopoverAnchorRef,
      activateSameValueForElement,
      updateCellPopoverForElement,
      focusIdentityLocationInCurrentRenderer,
      popoverItems,
    } = useIxdSameValuePopover({
      containerRef,
      focusedCellRef,
      selectCellRef,
      identityLocationsByKey,
      cellNotices,
      onFocusIdentityLocation,
      getModifiedValues,
    });

    // 선택된 uniqueId 목록 → 중복 없는 data-cell-id 목록으로 변환해 부모로 전파한다.
    // IXD 선택 → DSD dim 강조에 쓰인다. identity key 를 함께 전파해, 동일 cellId 로
    // 충돌하는 다른 축 셀의 매칭 정보와 섞이지 않게 한다.
    //
    // DL 도 함께 싣는다 — cellId 도 identity 도 DL 을 담지 않아, 같은 fact 를 두 DL
    // 이 태깅하면 소비자가 어느 DL 에서 고른 선택인지 알 방법이 없다.
    const emitSelectedCellIds = useCallback(
      (uniqueIds: string[]) => {
        if (!onSelectedCellIdsChange) return;
        if (!containerRef.current) {
          onSelectedCellIdsChange([]);
          return;
        }
        const cellIds: string[] = [];
        const identityKeys: Array<string | null> = [];
        const dlViewIds: Array<string | null> = [];
        const seen = new Set<string>();
        for (const uid of uniqueIds) {
          const cell = containerRef.current.querySelector(
            `#${CSS.escape(uid)}`,
          ) as HTMLElement | null;
          const cellId = cell?.getAttribute("data-cell-id");
          if (!cellId) continue;
          const identityKey =
            cell?.getAttribute("data-cell-identity-key") ?? null;
          // 두 값을 잇는 구분자는 NUL — cellId·identityKey 어디에도 안 나오는 문자라
          // 경계가 섞이지 않는다. 원문 바이트로 넣으면 grep 이 이 파일을 바이너리로
          // 취급해 검색에서 통째로 빠지므로 escape 로 쓴다.
          const dedupKey = `${cellId}\u0000${identityKey ?? ""}`;
          if (seen.has(dedupKey)) continue;
          seen.add(dedupKey);
          cellIds.push(cellId);
          identityKeys.push(identityKey);
          dlViewIds.push(
            cell?.closest(IXD_SECTION_SELECTOR)?.getAttribute("data-dl-id") ??
              null,
          );
        }
        onSelectedCellIdsChange(cellIds, identityKeys, dlViewIds);
      },
      [onSelectedCellIdsChange],
    );

    // 마지막 단일 선택 셀 — 편집/undo 직후 popover 재평가에 사용한다.
    const lastSingleSelectedCellRef = useRef<HTMLElement | null>(null);

    const handleSelectionChange = useCallback(
      (uniqueIds: string[]) => {
        // 단일 셀 선택 시 동일값 linkage popover 를 띄운다.
        if (uniqueIds.length !== 1 || !containerRef.current) {
          lastSingleSelectedCellRef.current = null;
          setCellPopover((previous) => (previous ? null : previous));
          clearFocusedCell(focusedCellRef);
        } else {
          const uid = uniqueIds[0];
          const cell = containerRef.current.querySelector(
            `#${CSS.escape(uid)}`,
          ) as HTMLElement | null;
          lastSingleSelectedCellRef.current = cell;
          if (!cell) {
            setCellPopover((previous) => (previous ? null : previous));
            clearFocusedCell(focusedCellRef);
          } else {
            updateCellPopoverForElement(cell);
          }
        }

        // 일반 보기 모드(편집 의도 없음)에서 IXD 선택을 DSD 강조용으로 전파한다.
        // linkage popover 와 공존하며, 편집 중에는 강조 대상이 아니므로 전파하지 않는다.
        if (!editable) emitSelectedCellIds(uniqueIds);
      },
      [
        editable,
        emitSelectedCellIds,
        updateCellPopoverForElement,
        setCellPopover,
      ],
    );

    // 편집·undo 는 선택을 바꾸지 않은 채 수정 상태만 바꾼다. getModifiedValues 는
    // 세션 상태(cellChanges/undoStack)에 의존해 매 편집마다 identity 가 바뀌므로,
    // 그때마다 선택 셀 기준으로 popover 를 다시 평가한다 (열기·내용 갱신·닫기).
    useEffect(() => {
      if (!getModifiedValues) return;
      const cell = lastSingleSelectedCellRef.current;
      if (cell?.isConnected) updateCellPopoverForElement(cell);
    }, [getModifiedValues, updateCellPopoverForElement]);

    // useIxdCellEditHandlers가 td에서 직접 fsType을 추출해 entry에 담는다.
    // renderer는 onCellEditBatch를 그대로 전달.
    const { handleEnterEdit, handlePasteValues, handleDeleteValues } =
      useIxdCellEditHandlers(containerRef, {
        editable,
        onCellEditBatch,
      });

    // chunk별 fsType-bound onCellEdit을 사전 memo.
    // chunks.map 내부에서 매 render마다 inline 함수를 만들면 Chunk(memo)의
    // onCellEdit ref가 매번 달라져, selection setState 같은 가벼운 re-render
    // 에도 모든 chunk/dlView/cell이 재렌더되어 키보드 이동이 체감상 멈춘다.
    const chunkOnCellEditByChunkId = useMemo(() => {
      const map = new Map<
        string,
        (
          cellId: string,
          newValue: string,
          identityKey?: string,
          locationUid?: string,
        ) => void
      >();
      if (!onCellEdit) return map;
      for (const chunk of chunks) {
        const fs = chunkFsTypeById?.get(chunk.id);
        if (!fs) continue;
        map.set(chunk.id, (cellId, newValue, identityKey, locationUid) =>
          onCellEdit(fs, cellId, newValue, identityKey, locationUid),
        );
      }
      return map;
    }, [chunks, chunkFsTypeById, onCellEdit]);

    const {
      handleCellMouseDown,
      handleCellMouseEnter,
      handleCellMouseUp,
      selectCell,
      selectCells,
    } = useIxdCellSelection(containerRef, {
      onEnterEdit: editable ? handleEnterEdit : undefined,
      onPasteValues: editable ? handlePasteValues : undefined,
      onDeleteValues: editable ? handleDeleteValues : undefined,
      // 비교 화면은 chunk마다 IxdPreviewRenderer를 마운트하므로 document-level
      // keydown 리스너가 N개 등록된다. undo는 selection과 무관하게 동작해야
      // 하지만(편집 후 선택 풀린 상태에서도 호출 가능), 그 의도가 활성 renderer
      // 하나에만 적용되도록 selectedChunkId를 가진 renderer에서만 listener를
      // 등록한다. 그렇지 않으면 한 Ctrl+Z에 undo가 chunk 개수만큼 중복 실행된다.
      // selectedChunkId가 일시적으로 null인 시점에 Ctrl+Z 못 받는 비용보다
      // 중복 발화 위험이 큼.
      onUndo:
        editable &&
        selectedChunkId !== null &&
        selectedChunkId !== undefined &&
        currentChunkIds.has(selectedChunkId)
          ? onUndo
          : undefined,
      onSelectionChange: handleSelectionChange,
      enableRelatedCellSelection: true,
    });

    useEffect(() => {
      selectCellRef.current = selectCell;
    }, [selectCell]);

    // 헤더(th) 클릭 → 같은 표 내 대응 데이터 셀 선택(파란 강조). 선택 변경이 onSelectionChange
    // 경로를 타고 DSD dim 까지 드래그와 동일하게 전파된다. 편집 모드에선 끈다(편집 흐름 충돌 방지).
    // 값 셀(td) 클릭/드래그는 useIxdCellSelection 의 handleSelectionChange 가 처리한다.
    const handleContainerClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (editable) return;
        const target = event.target as HTMLElement;

        const cells = collectIxdCellsForHeader(target);
        if (cells && cells.length > 0) {
          const uniqueIds = cells.map((cell) => cell.id).filter(Boolean);
          if (uniqueIds.length > 0) {
            // 값 셀 mousedown 과 동일하게 IXD 선택은 DSD 클릭 focus 를 정리한다.
            onClearFocus?.();
            selectCells(uniqueIds);
            return;
          }
        }

        // 값 셀(td[data-cell-id]) 클릭은 mousedown 선택이 이미 처리하므로 건드리지 않는다.
        if (target.closest("[data-cell-id]")) return;
      },
      [editable, selectCells, onClearFocus],
    );

    const handleIxdCellMouseDown = useCallback(
      (cellId: string, event: React.MouseEvent) => {
        // onClearFocus 경로가 없는 경우(export viewer 등)를 위해 로컬 clear는 남겨둔다.
        // onClearFocus가 있으면 그 내부에서도 clearFocus를 호출하므로 의도적 중복이다.
        clearFocusedCell(focusedCellRef);
        onClearFocus?.();
        handleCellMouseDown(cellId, event);
      },
      [handleCellMouseDown, onClearFocus],
    );

    // 외부에서 호출 가능한 메서드 노출
    useImperativeHandle(ref, () => ({
      searchByCellIds: (
        cellIds: string[],
        expectedIdentityKeysByCellId?: Map<string, string[]>,
      ) => {
        if (!cellIds?.length || !containerRef.current) return [];
        // 동일 cellId 가 축만 다른 물리 셀에 중복될 수 있다. 매칭 fact 의 identity
        // 를 아는 cellId 는 `data-cell-identity-key` 로 **직접 선택**한다 (양성
        // 선택) — cellId 검색 후 제외하는 방식은 attr 누락/오염 시 새기 때문.
        // identity 조회가 빈 결과이거나 기대 미등록 cellId(구버전 레코드, 블록/
        // 문장 매칭)만 cellId 검색으로 fallback 한다.
        const elements: HTMLElement[] = [];
        const seen = new Set<HTMLElement>();
        const identityKeys = new Set<string>();
        const addElement = (el: HTMLElement, expected: Set<string> | null) => {
          if (seen.has(el)) return;
          const identityKey = el.getAttribute("data-cell-identity-key");
          if (expected && identityKey && !expected.has(identityKey)) return;
          seen.add(el);
          elements.push(el);
          if (identityKey) identityKeys.add(identityKey);
        };
        for (const id of cellIds) {
          const expectedList = expectedIdentityKeysByCellId?.get(id);
          const expected = expectedList?.length ? new Set(expectedList) : null;
          if (expectedList?.length) {
            let foundByIdentity = false;
            for (const key of expectedList) {
              const identityEls = containerRef.current.querySelectorAll(
                `[data-cell-identity-key="${CSS.escape(key)}"]`,
              ) as NodeListOf<HTMLElement>;
              identityEls.forEach((el) => addElement(el, null));
              foundByIdentity = foundByIdentity || identityEls.length > 0;
            }
            if (foundByIdentity) continue;
          }
          const els = containerRef.current.querySelectorAll(
            exactCellIdSelector(id),
          ) as NodeListOf<HTMLElement>;
          els.forEach((el) => addElement(el, expected));
          // 문장영역 태그는 period 가 빈 정본으로 매칭되는데 표는 기간 열에 그린다.
          // 정확 일치가 0 건일 때만 period 를 무시하고 다시 찾는다.
          if (els.length === 0 && isPeriodlessCellId(id)) {
            (
              containerRef.current.querySelectorAll(
                periodAgnosticCellIdSelector(id),
              ) as NodeListOf<HTMLElement>
            ).forEach((el) => addElement(el, expected));
          }
        }
        identityKeys.forEach((identityKey) => {
          const relatedEls = containerRef.current?.querySelectorAll(
            `[data-cell-identity-key="${CSS.escape(identityKey)}"]`,
          ) as NodeListOf<HTMLElement> | undefined;
          // 필터를 통과한 identity 의 동일 fact 셀 확장이므로 추가 필터 불필요.
          relatedEls?.forEach((el) => addElement(el, null));
        });
        elements.sort((a, b) => {
          const position = a.compareDocumentPosition(b);
          if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
          if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
          return 0;
        });
        return elements;
      },

      searchHeaderByTag: ({ lineItem, period, member }) => {
        const root = containerRef.current;
        if (!root) return [];
        const byId = (id: string) =>
          Array.from(
            root.querySelectorAll<HTMLElement>(
              `th[data-header-ids~="${CSS.escape(id)}"]`,
            ),
          );
        // 우선순위: 라인아이템 > 축 멤버 > 기간.
        if (lineItem) {
          const els = byId(lineItem);
          if (els.length) return els;
        }
        if (member) {
          const els = byId(member);
          if (els.length) return els;
        }
        if (period) {
          return Array.from(
            root.querySelectorAll<HTMLElement>(
              `th[data-header-period="${CSS.escape(period)}"]`,
            ),
          );
        }
        return [];
      },

      activateSameValueForCell: (cell: HTMLElement) =>
        activateSameValueForElement(cell),

      focusCellLocation: (
        location: CellIdentityLocation,
        options?: FocusIdentityLocationOptions,
      ) => focusIdentityLocationInCurrentRenderer(location, options),

      clearFocus: () => {
        clearFocusedCell(focusedCellRef);
      },

      scrollToChunk: (chunkId: string) => {
        if (!containerRef.current) return;

        const chunkElement = containerRef.current.querySelector(
          `[data-chunk-id="${chunkId}"]`,
        ) as HTMLElement;

        if (chunkElement) {
          // chunk 는 content-visibility:auto 라 화면 밖에서는 placeholder 높이다.
          // 한 번에 스크롤하면 지나온 chunk 들이 실제 높이로 펴지며 목표가 밀리므로,
          // 위치가 멈출 때까지 프레임마다 다시 맞춘다 (smooth 는 그 사이 계속 취소된다).
          let previousTop = Number.NaN;
          let attempts = 0;
          const align = () => {
            chunkElement.scrollIntoView({ block: "start" });
            const top = chunkElement.getBoundingClientRect().top;
            if (
              top !== previousTop &&
              ++attempts < CHUNK_SCROLL_MAX_ALIGN_FRAMES
            ) {
              previousTop = top;
              window.requestAnimationFrame(align);
            }
          };
          align();
          chunkElement.style.transition = "background-color 0.3s ease";
          chunkElement.style.backgroundColor = "rgba(147, 51, 234, 0.1)";
          window.setTimeout(() => {
            chunkElement.style.backgroundColor = "";
          }, CHUNK_HIGHLIGHT_RESET_DELAY_MS);
        }
      },

      getScrollContainer: () => containerRef.current,
    }));

    if (chunks.length === 0) {
      return (
        <div className="flex items-center justify-center h-full p-10 text-center text-gray-400">
          <span>IXD 데이터가 없습니다.</span>
        </div>
      );
    }

    return (
      <IxdPreviewConfigProvider
        editable={editable}
        cellNotices={cellNotices}
        identityLocationsByKey={identityLocationsByKey}
        showCellFootnotes={showCellFootnotes}
        showRawValueInEdit={showRawValueInEdit}
        onCellEditStart={onCellEditStart}
        onCellMouseDown={handleIxdCellMouseDown}
        onCellMouseEnter={handleCellMouseEnter}
        onCellMouseUp={handleCellMouseUp}
      >
        <div
          ref={containerRef}
          id={reviewStyleScopeId}
          onClick={handleContainerClick}
          className={cn(
            // 여기에 padding-bottom을 넣지 말 것. 이 div가 세로 스크롤 컨테이너이고
            // TableScrollArea(src/toggle-ui-kit/table-scroll-area.tsx)의 가로 스크롤바가
            // `sticky bottom-0`으로 이 스크롤포트에 붙는데, 브라우저가 sticky 제약을
            // 스크롤 컨테이너의 content box 기준으로 잡아 스크롤바가 padding-bottom만큼
            // 위로 떠 보인다. 하단 여백은 아래 in-flow 스페이서로 확보한다.
            "w-full h-full overflow-auto px-5 pt-5 font-sans text-sm text-gray-700 bg-white",
            // 헤더/패딩헤더(th) 클릭으로 대응 셀을 선택할 수 있음을 cursor 로 알림(편집 모드 제외).
            !editable && "[&_th]:cursor-pointer",
            className,
          )}
        >
          {chunks.map((chunk, index) => {
            const showChunkSeparator = index > 0 || showLeadingChunkSeparator;
            const chunkFsType = chunkFsTypeById?.get(chunk.id);
            // 이 chunk에 속한 셀의 fsType은 자명하므로(consolidated/separate chunks
            // 배열 분류 기준), 여기서 onCellEdit을 fsType-bound로 감싸 내부
            // 컴포넌트에는 (cellId, newValue) signature 그대로 노출한다.
            if (editable && onCellEdit && !chunkFsType && import.meta.env.DEV) {
              // 비교 화면의 chunkDetail과 chunkFsTypeById(editor preview 기반)의
              // chunk id 정렬이 깨지면 인라인 편집이 silent로 무동작이 된다.
              // 정상 경로에선 발생하지 않으므로 dev 환경에서만 회귀 감지용 경고.
              console.warn(
                "[IxdPreviewRenderer] chunk fsType 매핑 누락 — 인라인 편집이 무시됩니다.",
                { chunkId: chunk.id },
              );
            }
            const chunkOnCellEdit = chunkOnCellEditByChunkId.get(chunk.id);

            return (
              <div
                key={chunk.id}
                className={cn(
                  showChunkSeparator &&
                    "mt-10 pt-10 border-t-2 border-label-assistive",
                )}
                data-ixd-fs-type={chunkFsType}
              >
                <Chunk
                  chunk={chunk}
                  renderScopeId={
                    chunks.length === 1 ? renderScopeId : undefined
                  }
                  sourceDlViewIndexOffset={
                    chunks.length === 1 ? sourceDlViewIndexOffset : undefined
                  }
                  isSelected={chunk.id === selectedChunkId}
                  showPeriodLabels={showPeriodLabels}
                  hideBeforePreviousPeriod={hideBeforePreviousPeriod}
                  cellChanges={cellChanges}
                  changeOrigins={changeOrigins}
                  cellIdentityByNormalizedCellId={
                    cellIdentityByNormalizedCellId
                  }
                  identityChanges={identityChanges}
                  onCellEdit={chunkOnCellEdit}
                />
              </div>
            );
          })}
          {/* 컨테이너 padding-bottom 대체 하단 여백 — 위 className 주석 참고. */}
          {showBottomSpacer && <div aria-hidden className="h-10" />}
          {/* 연동 셀 옆 floating panel(CellLinkagePopover)은 compare·editor 공통으로 노출된다. */}
          <CellLinkagePopover
            cellPopover={cellPopover}
            anchorRef={cellPopoverAnchorRef}
            items={popoverItems}
            modifiedValues={
              cellPopover
                ? (getModifiedValues?.(cellPopover.element) ?? null)
                : null
            }
            notice={
              cellPopover
                ? (cellNotices?.get(
                    cellPopover.element.getAttribute(
                      "data-cell-identity-key",
                    ) ?? "",
                  ) ?? null)
                : null
            }
            onClose={() => setCellPopover(null)}
          />
        </div>
      </IxdPreviewConfigProvider>
    );
  },
);

IxdPreviewRenderer.displayName = "IxdPreviewRenderer";
